import { Lock, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Subscription-gating UI overlay. Use whenever a feature is locked:
 *   <AccessGate access={accessRecord} feature="Companion App">
 *     <YourFeatureUI />
 *   </AccessGate>
 *
 * If access.has_access is true → renders children as-is.
 * Otherwise → renders an upgrade-prompt card with explicit verbiage that
 * the patient WILL keep all their historical data (per user spec 2026-04-26).
 *
 * `compact=true` renders the inline launcher chip (used on the dashboard
 * for the Companion App card). `compact=false` renders the full upgrade page.
 */
export default function AccessGate({ access, feature = "this feature", children, compact = false }) {
  if (!access || access.has_access) {
    return children;
  }
  if (compact) {
    return (
      <div className="h-full rounded-2xl bg-gradient-to-br from-slate-200 to-slate-100 dark:from-slate-800 dark:to-slate-900 border border-slate-300 dark:border-slate-700 p-5 flex flex-col justify-between min-h-[360px]" data-testid="access-gate-compact">
        <div className="flex items-start gap-4">
          <div className="text-3xl opacity-60 flex-shrink-0">🔒</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-500">Subscription perk</p>
            <h3 className="text-base font-bold leading-tight text-slate-900 dark:text-white mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {feature} — locked
            </h3>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Subscribe to unlock. Your past logs stay safe and reappear when you re-subscribe.</p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <Link to="/medications" className="w-full text-sm font-bold rounded-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 inline-flex items-center justify-center gap-1.5" data-testid="access-gate-cta">
            <Sparkles className="w-3.5 h-3.5" /> Unlock
          </Link>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 text-center">
            Got a code from our team?{" "}
            <Link to="/profile#companion-access" className="text-purple-600 dark:text-purple-400 hover:underline font-semibold" data-testid="access-gate-compact-have-code">
              Redeem →
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="access-gate-full">
      <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-purple-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {feature} is a subscription perk
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
          Subscribers unlock food logging, macro rings, Fit-Quiz projection, an AI wellness assistant (not medical advice — clinician review always separate), and the 5-week weigh-in reward. The free Companion tabs (Rewards, Shot Reminders, Body tracking) still work without a subscription.
        </p>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 my-4 text-left">
          <p className="text-xs text-emerald-800 dark:text-emerald-200 font-semibold mb-1">📦 Your data is safe</p>
          <p className="text-[11px] text-emerald-700 dark:text-emerald-300 leading-relaxed">
            All your past logs (weight, calories, exercise, symptoms, coupons earned) are <strong>permanently saved</strong>. The moment you re-subscribe, everything reappears exactly where you left off.
          </p>
        </div>
        <p className="text-xs text-slate-500 mb-5">
          {access?.reason === "cancelled_or_inactive" ? (
            <>Your subscription has ended. <strong>Resubscribe to instantly restore access.</strong></>
          ) : (
            <>You&apos;re currently on a one-time order. Switch to a subscription to unlock everything.</>
          )}
        </p>
        <Link
          to="/medications"
          className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl py-3 inline-flex items-center justify-center gap-2"
          data-testid="access-gate-full-cta"
        >
          <Sparkles className="w-4 h-4" /> Subscribe & unlock <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/profile#companion-access"
          className="block mt-3 text-xs text-purple-600 dark:text-purple-400 hover:underline font-semibold"
          data-testid="access-gate-have-code"
        >
          Have an access code? Redeem on your profile →
        </Link>
        <Link to="/dashboard" className="block mt-2 text-xs text-slate-400 hover:text-slate-700">
          Back to dashboard
        </Link>
      </div>
    </div>
  );
}
